// /src/pages/admin/Usuarios.tsx
import React, { useState } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Select,
  useDisclosure,
  useToast,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { IUsuario, UserRole } from '../../types';
import { mockUsuarios } from '../../mocks/usuarios';
import { mockClientes } from '../../mocks/clientes';

export const Usuarios: React.FC = () => {
  const [usuarios, setUsuarios] = useState<IUsuario[]>(mockUsuarios);
  const [usuarioEditando, setUsuarioEditando] = useState<IUsuario | null>(null);
  const [nuevoUsuario, setNuevoUsuario] = useState<Partial<IUsuario>>({
    nombre: '',
    email: '',
    rol: 'Cliente',
    empresaId: '',
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const toast = useToast();

  const handleCrearUsuario = () => {
    if (!nuevoUsuario.nombre || !nuevoUsuario.email || !nuevoUsuario.rol) {
      toast({
        title: 'Error',
        description: 'Completa todos los campos obligatorios',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    // Validar que si es Cliente, tenga empresaId
    if (nuevoUsuario.rol === 'Cliente' && !nuevoUsuario.empresaId) {
      toast({
        title: 'Error',
        description: 'Los usuarios tipo Cliente deben tener una empresa asignada',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    const usuario: IUsuario = {
      id: `user-${Date.now()}`,
      nombre: nuevoUsuario.nombre!,
      email: nuevoUsuario.email!,
      rol: nuevoUsuario.rol as UserRole,
      empresaId: nuevoUsuario.rol === 'Cliente' ? nuevoUsuario.empresaId : undefined,
    };

    setUsuarios([...usuarios, usuario]);
    
    toast({
      title: 'Usuario creado',
      description: `${usuario.nombre} ha sido agregado exitosamente`,
      status: 'success',
      duration: 3000,
    });

    setNuevoUsuario({ nombre: '', email: '', rol: 'Cliente', empresaId: '' });
    onClose();
  };

  const handleEditarUsuario = () => {
    if (!usuarioEditando) return;

    if (!usuarioEditando.nombre || !usuarioEditando.email || !usuarioEditando.rol) {
      toast({
        title: 'Error',
        description: 'Completa todos los campos obligatorios',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    // Validar que si es Cliente, tenga empresaId
    if (usuarioEditando.rol === 'Cliente' && !usuarioEditando.empresaId) {
      toast({
        title: 'Error',
        description: 'Los usuarios tipo Cliente deben tener una empresa asignada',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setUsuarios(usuarios.map(u => u.id === usuarioEditando.id ? usuarioEditando : u));
    
    toast({
      title: 'Usuario actualizado',
      description: `${usuarioEditando.nombre} ha sido actualizado exitosamente`,
      status: 'success',
      duration: 3000,
    });

    setUsuarioEditando(null);
    onEditClose();
  };

  const handleEliminarUsuario = (id: string) => {
    const usuario = usuarios.find(u => u.id === id);
    setUsuarios(usuarios.filter(u => u.id !== id));
    
    toast({
      title: 'Usuario eliminado',
      description: `${usuario?.nombre} ha sido eliminado`,
      status: 'info',
      duration: 3000,
    });
  };

  const abrirModalEditar = (usuario: IUsuario) => {
    setUsuarioEditando({ ...usuario });
    onEditOpen();
  };

  const getRolBadge = (rol: UserRole) => {
    const colorScheme = {
      'Admin': 'purple',
      'Consultor': 'blue',
      'Cliente': 'green',
    }[rol];

    return <Badge colorScheme={colorScheme}>{rol}</Badge>;
  };

  const getNombreEmpresa = (empresaId?: string) => {
    if (!empresaId) return '-';
    const empresa = mockClientes.find(c => c.id === empresaId);
    return empresa?.nombre || '-';
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={6}>
        <Heading size="lg">Gestión de Usuarios</Heading>
        <Button colorScheme="blue" onClick={onOpen}>
          + Crear Usuario
        </Button>
      </Box>

      <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th>Nombre</Th>
              <Th>Email</Th>
              <Th>Rol</Th>
              <Th>Empresa</Th>
              <Th>Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {usuarios.map((usuario) => (
              <Tr key={usuario.id}>
                <Td fontWeight="medium">{usuario.nombre}</Td>
                <Td>{usuario.email}</Td>
                <Td>{getRolBadge(usuario.rol)}</Td>
                <Td>{getNombreEmpresa(usuario.empresaId)}</Td>
                <Td>
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      icon={<span>⋮</span>}
                      variant="ghost"
                      size="sm"
                    />
                    <MenuList>
                      <MenuItem onClick={() => abrirModalEditar(usuario)}>
                        ✏️ Editar
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleEliminarUsuario(usuario.id)}
                        color="red.500"
                      >
                        🗑️ Eliminar
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Modal para crear usuario */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Crear Nuevo Usuario</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4} isRequired>
              <FormLabel>Nombre Completo</FormLabel>
              <Input
                value={nuevoUsuario.nombre}
                onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, nombre: e.target.value })}
                placeholder="Juan Pérez"
              />
            </FormControl>

            <FormControl mb={4} isRequired>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                value={nuevoUsuario.email}
                onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, email: e.target.value })}
                placeholder="juan@ejemplo.com"
              />
            </FormControl>

            <FormControl mb={4} isRequired>
              <FormLabel>Rol</FormLabel>
              <Select
                value={nuevoUsuario.rol}
                onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, rol: e.target.value as UserRole, empresaId: '' })}
              >
                <option value="Admin">Admin</option>
                <option value="Consultor">Consultor</option>
                <option value="Cliente">Cliente</option>
              </Select>
            </FormControl>

            {nuevoUsuario.rol === 'Cliente' && (
              <FormControl isRequired>
                <FormLabel>Empresa</FormLabel>
                <Select
                  placeholder="Selecciona una empresa"
                  value={nuevoUsuario.empresaId}
                  onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, empresaId: e.target.value })}
                >
                  {mockClientes.map((cliente) => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.nombre}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button colorScheme="blue" onClick={handleCrearUsuario}>
              Crear Usuario
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal para editar usuario */}
      <Modal isOpen={isEditOpen} onClose={onEditClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Editar Usuario</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {usuarioEditando && (
              <>
                <FormControl mb={4} isRequired>
                  <FormLabel>Nombre Completo</FormLabel>
                  <Input
                    value={usuarioEditando.nombre}
                    onChange={(e) => setUsuarioEditando({ ...usuarioEditando, nombre: e.target.value })}
                    placeholder="Juan Pérez"
                  />
                </FormControl>

                <FormControl mb={4} isRequired>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    value={usuarioEditando.email}
                    onChange={(e) => setUsuarioEditando({ ...usuarioEditando, email: e.target.value })}
                    placeholder="juan@ejemplo.com"
                  />
                </FormControl>

                <FormControl mb={4} isRequired>
                  <FormLabel>Rol</FormLabel>
                  <Select
                    value={usuarioEditando.rol}
                    onChange={(e) => setUsuarioEditando({ ...usuarioEditando, rol: e.target.value as UserRole, empresaId: undefined })}
                  >
                    <option value="Admin">Admin</option>
                    <option value="Consultor">Consultor</option>
                    <option value="Cliente">Cliente</option>
                  </Select>
                </FormControl>

                {usuarioEditando.rol === 'Cliente' && (
                  <FormControl isRequired>
                    <FormLabel>Empresa</FormLabel>
                    <Select
                      placeholder="Selecciona una empresa"
                      value={usuarioEditando.empresaId}
                      onChange={(e) => setUsuarioEditando({ ...usuarioEditando, empresaId: e.target.value })}
                    >
                      {mockClientes.map((cliente) => (
                        <option key={cliente.id} value={cliente.id}>
                          {cliente.nombre}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditClose}>
              Cancelar
            </Button>
            <Button colorScheme="blue" onClick={handleEditarUsuario}>
              Guardar Cambios
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
